import * as i0 from '@angular/core';
import { Injectable, Component, Input } from '@angular/core';

class BcHelperService {
    getId(classRef) {
        const prefix = classRef.constructor.name;
        const array = new Uint32Array(1);
        const randomNumber = window.crypto.getRandomValues(array);
        return `${prefix}.${randomNumber[0]}`;
    }
    keyEvent(event, action) {
        if (event.key && event.key === 'Tab') {
            return true;
        }
        else if (event.key && (event.key === 'Enter' || event.key === ' ')) {
            if (action) {
                action.event();
            }
            return true;
        }
        if (event.type && event.type === 'focusout') {
            return false;
        }
        return true;
    }
    /**
     * Espera a que las fuentes del documento estén completamente cargadas.
     * Si `document.fonts?.ready` está disponible, espera a que esta promesa se resuelva.
     * Si `document.fonts` no está definido, la promesa se resuelve inmediatamente.
     *
     * @returns {Promise<void>} Una promesa que se resuelve cuando las fuentes están listas
     * o inmediatamente si `document.fonts` no está disponible.
     * @memberof BcHelperService
     */
    waitForFonts() {
        if (document.fonts?.ready) {
            return document.fonts.ready.then(() => { });
        }
        return new Promise((resolve) => resolve);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcHelperService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcHelperService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcHelperService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class BcGeneralInputComponent {
    constructor() {
        /**
         * @ignore
         * Método que se ejecuta cuando ocurre un cambio en el valor del input.
         *
         * @param m - El nuevo valor del input. Puede ser de cualquier tipo.
         */
        this.onChange = (m) => { };
        /**
         * @ignore
         * Método que se asigna como función de callback para manejar eventos de toque.
         * Este método es utilizado para cumplir con la interfaz de control de formularios
         * en Angular y puede ser sobrescrito según sea necesario.
         */
        this.onTouch = () => { };
    }
    /**
     * @ignore
     * Obtiene el valor actual del modelo asociado al componente.
     *
     * @returns El valor actual del modelo (`this.value`).
     */
    get model() {
        return this.value;
    }
    /**
     * @ignore
     * Escribe un valor en el componente de entrada general.
     *
     * @param value - El valor que se asignará al componente.
     */
    writeValue(value) {
        this.value = value;
    }
    /**
     * @ignore
     * Registra una función de callback que se invoca cuando el valor del control cambia.
     *
     * @param fn - Función de callback que se ejecutará cuando ocurra un cambio en el valor del control.
     */
    registerOnChange(fn) {
        this.onChange = fn;
    }
    /**
     * @ignore
     * Registra una función que será llamada cuando el control sea marcado como "tocado".
     * Esto es utilizado por Angular para manejar el estado de los formularios reactivos.
     *
     * @param fn - Función que será ejecutada cuando el control sea tocado.
     */
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    /**
     * @ignore
     * Establece un nuevo valor para la propiedad `value` y notifica el cambio
     * llamando al método `onChange`.
     *
     * @param value - El nuevo valor a asignar.
     */
    set(value) {
        this.value = value;
        this.onChange(this.value);
    }
    /**
     * @ignore
     * Agrega o elimina un valor de una colección dependiendo de su existencia.
     *
     * Si el valor ya está presente en la colección, lo elimina.
     * Si el valor no está presente, lo agrega.
     *
     * @param value - El valor que se desea agregar o eliminar.
     */
    addOrRemove(value) {
        if (this.contains(value)) {
            this.remove(value);
        }
        else {
            this.add(value);
        }
    }
    /**
     * @ignore
     * Verifica si el valor proporcionado está contenido en la propiedad `value`.
     *
     * - Si `value` es un arreglo, se comprueba si el valor proporcionado está presente en el arreglo.
     * - Si `value` no es un arreglo pero tiene un valor definido, se compara directamente con el valor proporcionado.
     * - Si `value` no está definido, devuelve `false`.
     *
     * @param value - El valor a verificar si está contenido en `value`.
     * @returns `true` si el valor está contenido, de lo contrario `false`.
     */
    contains(value) {
        if (this.value instanceof Array) {
            return this.value.indexOf(value) > -1;
        }
        else if (!!this.value) {
            return this.value === value;
        }
        return false;
    }
    /**
     * @ignore
     * Elimina un valor específico de la lista `value` si existe.
     * Si el valor no se encuentra en la lista o la lista es nula/indefinida, no realiza ninguna acción.
     *
     * @param value - El valor que se desea eliminar de la lista.
     */
    remove(value) {
        const index = this.value.indexOf(value);
        if (!this.value || index < 0) {
            return;
        }
        this.value.splice(index, 1);
        this.onChange(this.value);
    }
    /**
     * @ignore
     * Agrega un valor al conjunto de valores actuales si no está ya presente.
     *
     * - Si `this.value` es un arreglo, el valor se agrega al final del arreglo.
     * - Si `this.value` no es un arreglo, se convierte en un arreglo que contiene el valor.
     *
     * Después de agregar el valor, se llama al método `onChange` con el nuevo conjunto de valores.
     *
     * @param value - El valor que se desea agregar.
     */
    add(value) {
        if (!this.contains(value)) {
            if (this.value instanceof Array) {
                this.value.push(value);
            }
            else {
                this.value = [value];
            }
            this.onChange(this.value);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcGeneralInputComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: BcGeneralInputComponent, selector: "ng-component", inputs: { data: "data" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcGeneralInputComponent, decorators: [{
            type: Component,
            args: [{
                    template: ''
                }]
        }], propDecorators: { data: [{
                type: Input
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { BcGeneralInputComponent, BcHelperService };
